import React, {FC, useMemo} from 'react';
import styled from 'styled-components';

import {AsyncTableSource} from '../../../../../../../../src/components/common/table/source/asyncTableSource';
import {TableColumn} from '../../../../../../../../src/components/common/table/tableColumn';
import {TableRowRenderOptions} from '../../../../../../../../src/components/common/table/tableRow';
import {VirtualTable} from '../../../../../../../../src/components/common/table/virtualTable';
import {TableTokenPaginationDataModel} from '../../../../../../../../src/models/table/tablePaginationDataModel';
import {ContactsManagerPanelId} from '../../../../../../../../src/store/panels/panelStateBase';
import {HandlerOf, Renderer} from '../../../../../../../shared/helpers/src/react/reactHelpers';
import {accountsTableColumns, AccountsTableColumnsEnum} from '../common/accounts/accountsTableConfig';
import {contactsManagerAccountsSortColumns} from '../common/accounts/accountsTableHelpers';
import {AccountsTableRowContainer} from '../common/accounts/accountsTableRowContainer';
import {ContactsManagerCustomFieldColumnMetadata} from '../common/contactsManagerCustomFieldTypes';

/*
 * Props.
 */

interface AccountsTableProps {
  panelId: ContactsManagerPanelId;
  dynamicColumns: ReadonlyArray<
    TableColumn<AccountsTableColumnsEnum, ContactsManagerCustomFieldColumnMetadata | undefined>
  >;
  accountIds: ReadonlyArray<number>;
  selectedAccountIds: ReadonlyArray<number>;
  paginationData?: TableTokenPaginationDataModel;
  source: AsyncTableSource;

  renderEmptyState: Renderer;
  onAddSelectedAccountIds: HandlerOf<ReadonlyArray<number>>;
  onRemoveSelectedAccountIds: HandlerOf<ReadonlyArray<number>>;
}

/*
 * Styles.
 */

const StyledTableContainerDiv = styled.div`
  flex: 1;
  overflow: hidden;
  padding: 0 48px 0 36px;
  position: relative;
`;

/*
 * Component.
 */

export const AccountsSearchTable: FC<AccountsTableProps> = (props) => {
  const {
    panelId,
    accountIds,
    selectedAccountIds,
    dynamicColumns,
    paginationData,
    source,
    renderEmptyState,
    onAddSelectedAccountIds,
    onRemoveSelectedAccountIds,
  } = props;

  const sortedColumns = useMemo(
    () => contactsManagerAccountsSortColumns([accountsTableColumns[1], ...dynamicColumns]),
    [dynamicColumns],
  );
  const tableColumns = [accountsTableColumns[0], ...sortedColumns];

  const renderRow = (accountId: number, options: TableRowRenderOptions) => (
    <AccountsTableRowContainer
      panelId={panelId}
      accountId={accountId}
      isSelected={selectedAccountIds.includes(accountId)}
      options={options}
      tableColumns={tableColumns}
      onSelectAccount={(event) => {
        if (event.defaultPrevented) {
          return;
        }
        // Remove any selected account ids.
        onRemoveSelectedAccountIds(selectedAccountIds);
        onAddSelectedAccountIds([accountId]);
      }}
    />
  );

  return (
    <StyledTableContainerDiv>
      <VirtualTable<number>
        columns={tableColumns}
        source={source}
        items={accountIds}
        paginationData={paginationData}
        renderRow={renderRow}
        renderEmpty={renderEmptyState}
      />
    </StyledTableContainerDiv>
  );
};
